// src/components/achievements/CategoryCard.tsx - Korrigierte Version
import React, { useState, useMemo } from 'react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import type { Category, Achievement } from '../../services/achievementService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getDefaultCategoryIcon } from '../utils/iconHelper';
import { getBungieIconUrl } from '../../services/bungieIconService';

type Props = {
  category: Category;
  onClick: (category: Category) => void;
  userIsAuthenticated?: boolean;
};

// Rekursive Funktion, um alle Erfolge aus allen Unterkategorien zu sammeln
const collectAllAchievements = (category: Category): Achievement[] => {
  let allAchievements: Achievement[] = [...(category.achievements || [])];
  
  if (category.subcategories?.length) {
    category.subcategories.forEach(subcat => {
      allAchievements = [...allAchievements, ...collectAllAchievements(subcat)];
    });
  }
  
  return allAchievements;
};

const CategoryCard: React.FC<Props> = ({ 
  category, 
  onClick, 
  userIsAuthenticated = false
}) => {
  const { t } = useLanguage();
  const [iconError, setIconError] = useState(false);
  
  // Get the Bungie icon URL through our proxy if available
  const iconUrl = !iconError && category.icon_url ? getBungieIconUrl(category.icon_url) : null;
  
  // Sammle ALLE Erfolge aus dieser Kategorie und ALLEN Unterkategorien rekursiv
  const allAchievements = useMemo(() => {
    return collectAllAchievements(category);
  }, [category]);
  
  // Berechne die Fortschrittsstatistiken für alle gesammelten Erfolge
  const totalAchievements = allAchievements.length;
  const completedAchievements = userIsAuthenticated ? 
    allAchievements.filter(a => a.is_completed).length : 0;
  
  // Prüfe, ob alle Erfolge abgeschlossen sind
  const isFullyCompleted = totalAchievements > 0 && completedAchievements === totalAchievements;
  
  // Berechne den Fortschritt als Prozentsatz
  const completionPercentage = totalAchievements > 0
    ? Math.round((completedAchievements / totalAchievements) * 100)
    : 0;

  return (
    <div
      onClick={() => onClick(category)}
      className="bg-gray-800 rounded-lg overflow-hidden cursor-pointer hover:bg-gray-700 transition-all"
    >
      <div className="p-4 flex items-center">
        {iconUrl ? (
          <img
            src={iconUrl}
            alt={category.display_name}
            className="w-12 h-12 mr-4 rounded-md object-cover bg-gray-700"
            onError={(e) => {
              setIconError(true);
              const target = e.target as HTMLImageElement;
              target.onerror = null;
              target.src = getDefaultCategoryIcon(category.display_name);
            }}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-700 rounded-md mr-4 flex items-center justify-center">
            <img 
              src={getDefaultCategoryIcon(category.display_name)} 
              alt={category.display_name} 
              className="w-8 h-8"
            />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center">
            <h3 className="text-white font-medium">{category.display_name}</h3>
            {/* Show completion checkmark for authenticated users if all nested achievements are completed */}
            {userIsAuthenticated && isFullyCompleted && totalAchievements > 0 && (
              <CheckCircleIcon className="h-5 w-5 text-destiny-green ml-2" />
            )}
          </div>
          <div className="flex items-center mt-1 text-xs text-gray-400">
            <span>
              {totalAchievements > 0 ? `${totalAchievements} ${t('achievements.title')}` : ''}
              {category.subcategories?.length > 0 ? 
                (totalAchievements > 0 ? `, ` : '') + 
                `${category.subcategories.length} ${t('achievements.subcategories')}` : 
                (!totalAchievements ? t('achievements.noContent') : '')}
            </span>
            
            {/* Show completion stats for authenticated users (including nested achievements) */}
            {userIsAuthenticated && totalAchievements > 0 && (
              <span className="ml-2 text-destiny-gold">
                ({completedAchievements}/{totalAchievements}, {completionPercentage}%)
              </span>
            )}
          </div>
          
          {/* Progress bar - only for authenticated users and if there are any achievements */}
          <div className="mt-2 min-h-[4px]">
            {userIsAuthenticated && totalAchievements > 0 ? (
              <div className="w-full bg-gray-700 rounded-full h-1">
                <div
                  className={`h-1 rounded-full ${isFullyCompleted ? 'bg-destiny-green' : 'bg-destiny-blue'}`}
                  style={{ width: `${completionPercentage}%` }}
                ></div>
              </div>
            ) : (
              /* Empty spacer for consistent layout */
              <div className="w-full h-1"></div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryCard;